/**
 * Assumption profiles editor (spec §5): optimistic / average / pessimistic,
 * every field editable, SS haircut clearly labeled a policy assumption.
 */
import { useState } from 'react';
import type { Plan } from '../model/types';
import { NumberField } from './Inputs';
import { useFieldTip } from './FieldTip';
import { TIPS } from './tips';

interface Props {
  plan: Plan;
  update: (mut: (p: Plan) => void) => void;
}

/** Percent field with one-decimal precision (returns/inflation need it). */
function PercentField({
  label, value, onCommit, hint, min = 0, max = 20, tip,
}: {
  label: string; value: number; onCommit: (decimal: number) => void;
  hint?: string; min?: number; max?: number; tip?: string;
}) {
  const [text, setText] = useState<string | null>(null);
  const { handlers, bubble } = useFieldTip(tip);
  return (
    <div className="field" {...handlers}>
      <label>
        {label}
        <input
          inputMode="decimal"
          value={text ?? String(Math.round(value * 1000) / 10)}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            if (text != null) {
              const n = parseFloat(text);
              if (Number.isFinite(n)) onCommit(Math.min(max, Math.max(min, n)) / 100);
              setText(null);
            }
          }}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
      </label>
      {hint && <span className="hint">{hint}</span>}
      {bubble}
    </div>
  );
}

export function ProfilesTab({ plan, update }: Props) {
  return (
    <div>
      <p className="hint" style={{ marginTop: 0 }}>
        Three complete worldviews — every plan can be viewed through each (the switch is in the
        header). No hidden constants: everything the projection assumes is editable here.
      </p>
      {plan.assumptionProfiles.map((prof, i) => (
        <details key={prof.label} className="item-card" open={prof.label === plan.activeAssumptionLabel}>
          <summary>
            <strong style={{ textTransform: 'capitalize' }}>{prof.label}</strong>
            <span className="sub">
              stocks {(prof.returnsByAssetClass.equity * 100).toFixed(1)}% · inflation{' '}
              {(prof.generalInflation * 100).toFixed(1)}% · plan to {prof.longevityAge}
              {prof.socialSecurityHaircut ? ` · SS cut to ${Math.round(prof.socialSecurityHaircut.payableFraction * 100)}% from ${prof.socialSecurityHaircut.startYear}` : ''}
            </span>
          </summary>
          <div className="inputs-grid">
            <PercentField label="Stock return %/yr" value={prof.returnsByAssetClass.equity}
              tip={TIPS.equityReturn}
              onCommit={(v) => update((p) => { p.assumptionProfiles[i]!.returnsByAssetClass.equity = v; })} />
            <PercentField label="Bond return %/yr" value={prof.returnsByAssetClass.bond}
              tip={TIPS.bondReturn}
              onCommit={(v) => update((p) => { p.assumptionProfiles[i]!.returnsByAssetClass.bond = v; })} />
            <PercentField label="Cash return %/yr" value={prof.returnsByAssetClass.cash}
              onCommit={(v) => update((p) => { p.assumptionProfiles[i]!.returnsByAssetClass.cash = v; })} />
            <PercentField label="Inflation %/yr" value={prof.generalInflation}
              tip={TIPS.inflation}
              onCommit={(v) => update((p) => { p.assumptionProfiles[i]!.generalInflation = v; })} />
            <PercentField label="Healthcare inflation %/yr" value={prof.healthcareInflation}
              hint="Historically well above general inflation"
              onCommit={(v) => update((p) => { p.assumptionProfiles[i]!.healthcareInflation = v; })} />
            <PercentField label="Long-term-care inflation %/yr" value={prof.ltcInflation}
              onCommit={(v) => update((p) => { p.assumptionProfiles[i]!.ltcInflation = v; })} />
            <PercentField label="Wage growth %/yr" value={prof.wageGrowth}
              onCommit={(v) => update((p) => { p.assumptionProfiles[i]!.wageGrowth = v; })} />
            <PercentField label="Home appreciation %/yr" value={prof.housingAppreciation}
              onCommit={(v) => update((p) => { p.assumptionProfiles[i]!.housingAppreciation = v; })} />
            <NumberField label="Longevity age" value={prof.longevityAge} min={70} max={105}
              hint="The plan runs to whichever is later: this or each person's own setting"
              onCommit={(v) => update((p) => { p.assumptionProfiles[i]!.longevityAge = v; })} />
            <NumberField label="Social Security payable % (100 = full)" min={50} max={100}
              value={Math.round((prof.socialSecurityHaircut?.payableFraction ?? 1) * 100)}
              hint="Policy assumption, not a forecast: the 2025 Trustees Report projects the trust fund covers ~77% from 2033 absent legislation"
              onCommit={(v) => update((p) => {
                p.assumptionProfiles[i]!.socialSecurityHaircut =
                  v >= 100 ? undefined
                  : { payableFraction: v / 100, startYear: prof.socialSecurityHaircut?.startYear ?? 2033 };
              })} />
            {prof.socialSecurityHaircut && (
              <NumberField label="Haircut starts in year" min={2026} max={2060}
                value={prof.socialSecurityHaircut.startYear}
                onCommit={(v) => update((p) => {
                  p.assumptionProfiles[i]!.socialSecurityHaircut!.startYear = v;
                })} />
            )}
          </div>
        </details>
      ))}
    </div>
  );
}
